import cv from 'opencv-ts';

export const HueAdjustment = async (imageElement, params, outputCanvas) => {
  try {
    const { hueDelta, saturationScale } = params;
    // 转换颜色空间
    const src = cv.imread(imageElement);

    let hsv = new cv.Mat();
    cv.cvtColor(src, hsv, cv.COLOR_RGB2HSV, 0);

    // 分离 HSV 通道
    let hsvSplit = new cv.MatVector();
    cv.split(hsv, hsvSplit);

    if (hueDelta) {
      // 调整色相（H）
      let hue = hsvSplit.get(0); // 色相通道
      for (let i = 0; i < hue.rows; ++i) {
        for (let j = 0; j < hue.cols; ++j) {
          let pixel = hue.ucharPtr(i, j);
          let h = (pixel[0] + hueDelta) % 180;
          pixel[0] = h;
        }
      }
    }

    if (saturationScale) {
      // 调整饱和度（S）
      let saturation = hsvSplit.get(1); // 饱和度通道
      for (let i = 0; i < saturation.rows; ++i) {
        for (let j = 0; j < saturation.cols; ++j) {
          let pixel = saturation.ucharPtr(i, j);
          let s = cv.saturateCast(pixel[0] * saturationScale);
          pixel[0] = s;
        }
      }
    }

    // 合并 HSV 通道
    cv.merge(hsvSplit, hsv);
    const dst = new cv.Mat();

    cv.cvtColor(hsv, dst, cv.COLOR_HSV2RGB, 0);
    if (outputCanvas) {
      cv.imshow(outputCanvas, dst);
    }
    const imageData = dst.imageData;
    src.delete();
    hsv.delete();
    hsvSplit.delete();
    dst.delete();
    return imageData;
  } catch (error) {
    console.error('HueAdjustment error:', error);
    return error;
  }
};

// 通过一个图片地址生成一个加载完成后的HTMLImageElement
export const convertImageUrl2ImageElement = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      resolve(img);
    };
    img.src = url;
    img.onerror = () => {
      reject(null);
    };
  });
};
// 通过canvas生成一个HTMLImageElement
export const convertCanvas2ImageElement = (canvas) => {
  const url = canvas.toDataURL();
  return convertImageUrl2ImageElement(url);
};
// 色相处理值的函数
const hueValueModifyFn = (val, delta) => {
  // return (val + delta + 180) % 180;
  // 和ps一致处理
  const _delta = -2 * delta;
  return (val + _delta + 180) % 180;
};
// 饱和度处理值的函数
const saturationValueModifyFn = (val, delta) => {
  const deltaVal = val * (delta / 100);
  let finalVal = val + deltaVal;
  // 确保饱和度值在 0 到 255 的范围内
  finalVal = Math.max(0, Math.min(255, finalVal));
  return finalVal;
};
// 调整色相、饱和度和亮度的公共处理逻辑
export const handleHSVAdjustment = (srcElement, deltaValue, channelIdex = 0, valueModifyFn = null) => {
  try {
    console.time('HueAdjustmentHandler');
    const img = cv.imread(srcElement);
    const srcMat = new cv.Mat();
    cv.cvtColor(img, srcMat, cv.COLOR_BGR2HSV);

    let channelMatVector = new cv.MatVector();

    cv.split(srcMat, channelMatVector);
    const curChannel = channelMatVector.get(channelIdex);
    const height = curChannel.rows;
    const width = curChannel.cols;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let _value = curChannel.ucharPtr(y, x)[0];
        // 这里简单地将色相值增加一个值来修改色相
        if (typeof valueModifyFn === 'function') {
          _value = valueModifyFn(_value, deltaValue);
        }
        curChannel.ucharPtr(y, x)[0] = _value;
      }
    }

    cv.merge(channelMatVector, srcMat);
    const dstMat = new cv.Mat();
    cv.cvtColor(srcMat, dstMat, cv.COLOR_HSV2BGR);

    // 将处理后的图像输出到canvas上，方便后续的处理
    const canvas = document.createElement('canvas');
    cv.imshow(canvas, dstMat);

    img.delete();
    srcMat.delete();
    channelMatVector.delete();
    dstMat.delete();
    curChannel.delete();
    console.timeEnd('HueAdjustmentHandler');
    return canvas;
  } catch (error) {
    return null;
  }
};
// 调整色相
export const hueAdjustmentHandler = (srcElement, delta) => {
  try {
    return handleHSVAdjustment(srcElement, delta, 0, hueValueModifyFn);
  } catch (error) {
    return null;
  }
};
// 调整饱和度
export const saturationAdjustmentHandler = (srcElement, delta) => {
  try {
    return handleHSVAdjustment(srcElement, delta, 1, saturationValueModifyFn);
  } catch (error) {
    return null;
  }
};
// 调整亮度
export const valueAdjustmentHandler = (srcElement, delta) => {
  try {
    // 亮度的处理和饱和度类似
    return handleHSVAdjustment(srcElement, delta, 2, saturationValueModifyFn);
  } catch (error) {
    return null;
  }
};
// 调整对比度
export const contrastAdjustmentHandler = (srcElement, delta) => {
  try {
    console.time('contrastAdjustmentHandler');
    const imgMat = cv.imread(srcElement);
    const alpha = 1 + delta / 100;
    const beta = 0;

    const dstMat = new cv.Mat();
    cv.convertScaleAbs(imgMat, dstMat, alpha, beta);

    // 将处理后的图像输出到canvas上，方便后续的处理
    const canvas = document.createElement('canvas');
    cv.imshow(canvas, dstMat);

    imgMat.delete();
    dstMat.delete();
    console.timeEnd('contrastAdjustmentHandler');
    return canvas;
  } catch (error) {
    return null;
  }
};
// 调整透明度
export const alphaAdjustmentHandler = (srcElement, delta) => {
  try {
    console.time('alphaAdjustmentHandler');
    let srcMat = cv.imread(srcElement);
    const channels = srcMat.channels();
    if (channels === 3) {
      // 如果是RGB图像，添加alpha通道
      let rgbaMat = new cv.Mat();
      cv.cvtColor(srcMat, rgbaMat, cv.COLOR_BGR2BGRA);
      srcMat.delete();
      srcMat = rgbaMat;
    }
    const alphaValue = delta / 100;
    const rows = srcMat.rows;
    const cols = srcMat.cols;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        let pixel = srcMat.ptr(y, x);
        let newAlpha = Math.floor(255 * alphaValue);
        // 修改alpha通道的值
        pixel[3] = newAlpha;
      }
    }
    // 将处理后的图像输出到canvas上，方便后续的处理
    const canvas = document.createElement('canvas');
    cv.imshow(canvas, srcMat);

    srcMat.delete();
    console.timeEnd('alphaAdjustmentHandler');
    return canvas;
  } catch (error) {
    return null;
  }
};

// 追色功能，用新的图片的信息修改目标图片
export const addColorsByOtherImage = (targetImgDom, sourceImgDom, strength) => {
  return new Promise((resolve, reject) => {
    try {
      console.time('addColorsByOtherImage');
      let validStrength = strength < 10 ? 0 : strength > 100 ? 100 : strength;
      validStrength = validStrength / 100;
      // 强度等于0的时候直接返回原图
      if (!validStrength) {
        return resolve(targetImgDom);
      }
      // 读取目标图片
      const targetImg = cv.imread(targetImgDom);
      // 读取源图片
      const sourceImg = cv.imread(sourceImgDom);

      // 将图片转换为Lab颜色空间
      let targetMat = new cv.Mat();
      let referenceMat = new cv.Mat();

      cv.cvtColor(targetImg, targetMat, cv.COLOR_BGR2Lab);
      cv.cvtColor(sourceImg, referenceMat, cv.COLOR_BGR2Lab);

      let referenceMean = new cv.Mat();
      referenceMean.create(1, referenceMat.channels(), cv.CV_64F);
      let referenceStdDev = new cv.Mat();
      referenceStdDev.create(1, referenceMat.channels(), cv.CV_64F);
      cv.meanStdDev(referenceMat, referenceMean, referenceStdDev);
      let targetMean = new cv.Mat();
      targetMean.create(1, targetMat.channels(), cv.CV_64F);
      let targetStdDev = new cv.Mat();
      targetStdDev.create(1, targetMat.channels(), cv.CV_64F);
      cv.meanStdDev(targetMat, targetMean, targetStdDev);

      let coefficients = [];
      let offsets = [];

      for (let i = 0; i < referenceMat.channels(); i++) {
        let coefficient = (referenceStdDev.data64F[i] / targetStdDev.data64F[i]) * validStrength;
        let offset = (referenceMean.data64F[i] - coefficient * targetMean.data64F[i]) * validStrength;
        coefficients.push(coefficient);
        offsets.push(offset);
      }

      for (let row = 0; row < targetMat.rows; row++) {
        for (let col = 0; col < targetMat.cols; col++) {
          let pixel = targetMat.ptr(row, col);
          for (let i = 0; i < targetMat.channels(); i++) {
            let newVal = coefficients[i] * pixel[i] + offsets[i];
            // 确保值在合理范围内
            if (newVal < 0) {
              newVal = 0;
            } else if (newVal > 255) {
              newVal = 255;
            }
            pixel[i] = newVal;
          }
        }
      }

      let resultMat = new cv.Mat();
      cv.cvtColor(targetMat, resultMat, cv.COLOR_Lab2BGR);
      // let finalMat = new cv.Mat();
      // cv.addWeighted(targetImg, 1 - strength, resultMat, strength, 0, finalMat);

      // 在页面上显示追色后的图片
      const canvas = document.createElement('canvas');
      cv.imshow(canvas, resultMat);

      // const ctx = canvas.getContext('2d');

      let imgElement = new Image();
      imgElement.src = canvas.toDataURL();
      imgElement.onload = () => {
        resolve(imgElement);
      };

      // const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      // 释放内存
      targetImg.delete();
      sourceImg.delete();
      referenceMat.delete();
      targetMat.delete();
      referenceMean.delete();
      referenceStdDev.delete();
      targetMean.delete();
      targetStdDev.delete();
      resultMat.delete();
      // finalMat.delete();
      console.timeEnd('addColorsByOtherImage');
      return imgElement;
    } catch (error) {
      console.error(error);
      reject(null);
    }
  });
};
